import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  LIST_TOP_REFERRERS,
  GET_REFERRALS_BY_REFERRER,
  SEARCH_OWNERS,
  ACTIVATE_OWNER_REFERRAL,
  LIST_OWNERS,
} from '~/services/admin';
import {
  TopReferrer,
  ReferralDetail,
  DateRangePreset,
  ReferralStatus,
  ListTopReferrersResponse,
  GetReferralsByReferrerResponse,
  AdminOwner,
  ListOwnersResponse,
  SearchOwnersResponse,
  ActivateReferralResponse,
} from '~/types/admin';
import {
  CalendarIcon,
  ChevronRightIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'motion/react';

const ReferralsDashboard: React.FC = () => {
  const { t } = useTranslation('admin');

  // State management
  const [selectedReferrer, setSelectedReferrer] = useState<TopReferrer | null>(
    null,
  );
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [referralsPage, setReferralsPage] = useState(0);
  const [searchEmail, setSearchEmail] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [activeView, setActiveView] = useState<'referrers' | 'owners'>(
    'referrers',
  );
  const [ownersPage, setOwnersPage] = useState(0);
  const limit = 10;

  // Date range presets
  const datePresets: DateRangePreset[] = useMemo(
    () => [
      {
        label: t('referrals.date_presets.last_7_days'),
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        endDate: new Date(),
      },
      {
        label: t('referrals.date_presets.last_30_days'),
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        endDate: new Date(),
      },
      {
        label: t('referrals.date_presets.last_90_days'),
        startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        endDate: new Date(),
      },
      {
        label: t('referrals.date_presets.this_year'),
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: new Date(),
      },
    ],
    [t],
  );

  // GraphQL queries
  const {
    data: topReferrersData,
    loading: topReferrersLoading,
    error: topReferrersError,
    refetch: refetchTopReferrers,
  } = useQuery<{ list_top_referrers: ListTopReferrersResponse }>(
    LIST_TOP_REFERRERS,
    {
      variables: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit,
        offset: currentPage * limit,
      },
      errorPolicy: 'all',
    },
  );

  const {
    data: referralDetailsData,
    loading: referralDetailsLoading,
    refetch: refetchReferralDetails,
  } = useQuery<{ get_referrals_by_referrer: GetReferralsByReferrerResponse }>(
    GET_REFERRALS_BY_REFERRER,
    {
      variables: {
        referrerOwnerId: selectedReferrer?._id,
        limit,
        offset: referralsPage * limit,
      },
      skip: !selectedReferrer,
      errorPolicy: 'all',
    },
  );

  // Search owners query
  const {
    data: searchOwnersData,
    loading: searchOwnersLoading,
    refetch: refetchSearchOwners,
  } = useQuery<{ search_owners: any }>(SEARCH_OWNERS, {
    variables: {
      email: searchEmail,
      limit,
      offset: currentPage * limit,
    },
    skip: !isSearchMode || !searchEmail,
    errorPolicy: 'all',
  });

  // List all owners query
  const {
    data: ownersData,
    loading: ownersLoading,
    refetch: refetchOwners,
  } = useQuery<{ list_owners: ListOwnersResponse }>(LIST_OWNERS, {
    variables: {
      limit,
      offset: ownersPage * limit,
    },
    skip: activeView !== 'owners',
    errorPolicy: 'all',
  });

  // Activate referral mutation
  const [activateReferral, { loading: activatingReferral }] = useMutation<{
    activate_owner_referral: ActivateReferralResponse;
  }>(ACTIVATE_OWNER_REFERRAL, {
    onCompleted: (data) => {
      if (!data.activate_owner_referral.hasError) {
        // Refresh the owners list
        refetchOwners();
        // Show success message
        alert(t('referrals.activation_success'));
      } else {
        alert(data.activate_owner_referral.message);
      }
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  // Helper functions
  const handleDatePresetSelect = (preset: DateRangePreset) => {
    setDateRange({
      startDate: preset.startDate.toISOString().split('T')[0],
      endDate: preset.endDate.toISOString().split('T')[0],
    });
    setCurrentPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case ReferralStatus.SUBSCRIPTION_CREATED:
        return 'badge-success';
      case ReferralStatus.CHANNEL_CONNECTED:
        return 'badge-info';
      case ReferralStatus.AGENT_CREATED:
        return 'badge-warning';
      case ReferralStatus.ACCOUNT_CREATED:
        return 'badge-neutral';
      case ReferralStatus.REJECTED:
        return 'badge-error';
      default:
        return 'badge-neutral';
    }
  };

  const totalStats = useMemo(() => {
    const referrers =
      topReferrersData?.list_top_referrers?.data?.referrers || [];
    return referrers.reduce(
      (acc: any, referrer: any) => ({
        totalReferrals: acc.totalReferrals + referrer.total_referrals_count,
        effectiveReferrals:
          acc.effectiveReferrals + referrer.effective_referrals_count,
        accountsCreated:
          acc.accountsCreated + referrer.referral_stats.account_created,
        subscriptionsCreated:
          acc.subscriptionsCreated +
          referrer.referral_stats.subscription_created,
      }),
      {
        totalReferrals: 0,
        effectiveReferrals: 0,
        accountsCreated: 0,
        subscriptionsCreated: 0,
      },
    );
  }, [topReferrersData]);

  const handleActivateReferral = async (ownerId: string) => {
    if (confirm(t('referrals.confirm_activation'))) {
      try {
        await activateReferral({
          variables: { ownerId },
        });
      } catch (error) {
        console.error('Error activating referral:', error);
      }
    }
  };

  const handleRefreshAll = () => {
    refetchTopReferrers();
    if (activeView === 'owners') {
      refetchOwners();
    }
    if (selectedReferrer) {
      refetchReferralDetails();
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-base-content text-3xl font-bold">
              {t('referrals.title')}
            </h1>
            <p className="text-base-content/70 mt-1">
              {t('referrals.description')}
            </p>
          </div>

          <div className="flex gap-2">
            <div className="tabs tabs-boxed">
              <button
                className={`tab ${
                  activeView === 'referrers' ? 'tab-active' : ''
                }`}
                onClick={() => {
                  setActiveView('referrers');
                  setSelectedReferrer(null);
                }}
              >
                👥 {t('referrals.top_referrers')}
              </button>
              <button
                className={`tab ${activeView === 'owners' ? 'tab-active' : ''}`}
                onClick={() => {
                  setActiveView('owners');
                  setSelectedReferrer(null);
                  setOwnersPage(0);
                }}
              >
                <UserPlusIcon className="mr-1 h-4 w-4" />
                {t('referrals.manage_owners')}
              </button>
            </div>

            <button
              onClick={handleRefreshAll}
              className="btn btn-primary gap-2"
              disabled={topReferrersLoading || ownersLoading}
            >
              {topReferrersLoading || ownersLoading ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                <ArrowPathIcon className="h-5 w-5" />
              )}
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {/* Date Range Controls */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h3 className="card-title text-lg">{t('referrals.date_range')}</h3>

            {/* Date Presets */}
            <div className="mb-4 flex flex-wrap gap-2">
              {datePresets.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => handleDatePresetSelect(preset)}
                  className="btn btn-sm btn-outline"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom Date Range */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    {t('referrals.start_date')}
                  </span>
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, startDate: e.target.value })
                  }
                  className="input input-bordered"
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">{t('referrals.end_date')}</span>
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, endDate: e.target.value })
                  }
                  className="input input-bordered"
                />
              </div>
              <button
                onClick={() => {
                  setCurrentPage(0);
                  refetchTopReferrers();
                }}
                className="btn btn-primary gap-2"
              >
                <CalendarIcon className="h-5 w-5" />
                {t('referrals.apply_date_range')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="stat bg-base-100 rounded-box shadow-sm">
          <div className="stat-figure text-primary">
            <UserGroupIcon className="h-8 w-8" />
          </div>
          <div className="stat-title">
            {t('referrals.stats.total_referrals')}
          </div>
          <div className="stat-value text-primary">
            {totalStats.totalReferrals}
          </div>
        </div>

        <div className="stat bg-base-100 rounded-box shadow-sm">
          <div className="stat-figure text-success">
            <CheckCircleIcon className="h-8 w-8" />
          </div>
          <div className="stat-title">
            {t('referrals.stats.effective_referrals')}
          </div>
          <div className="stat-value text-success">
            {totalStats.effectiveReferrals}
          </div>
        </div>

        <div className="stat bg-base-100 rounded-box shadow-sm">
          <div className="stat-figure text-info">
            <UserGroupIcon className="h-8 w-8" />
          </div>
          <div className="stat-title">
            {t('referrals.stats.accounts_created')}
          </div>
          <div className="stat-value text-info">
            {totalStats.accountsCreated}
          </div>
        </div>

        <div className="stat bg-base-100 rounded-box shadow-sm">
          <div className="stat-figure text-warning">
            <CurrencyDollarIcon className="h-8 w-8" />
          </div>
          <div className="stat-title">{t('referrals.stats.subscriptions')}</div>
          <div className="stat-value text-warning">
            {totalStats.subscriptionsCreated}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Dynamic Content Based on Active View */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title">
              {activeView === 'owners'
                ? isSearchMode
                  ? t('referrals.search_results')
                  : t('referrals.all_owners')
                : isSearchMode
                  ? t('referrals.search_results')
                  : t('referrals.top_referrers')}
            </h2>

            {/* Search functionality for owners */}
            {activeView === 'owners' && (
              <div className="mb-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                  <div className="form-control flex-1">
                    <label className="label">
                      <span className="label-text">
                        {t('referrals.search_by_email')}
                      </span>
                    </label>
                    <input
                      type="email"
                      placeholder={t('referrals.email_address')}
                      className="input input-bordered"
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setIsSearchMode(true);
                          setCurrentPage(0);
                          setOwnersPage(0);
                          refetchSearchOwners();
                        }
                      }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      setIsSearchMode(true);
                      setCurrentPage(0);
                      setOwnersPage(0);
                      refetchSearchOwners();
                    }}
                    className="btn btn-primary gap-2"
                    disabled={!searchEmail.trim()}
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                    {t('common.search')}
                  </button>
                  {isSearchMode && (
                    <button
                      onClick={() => {
                        setIsSearchMode(false);
                        setSearchEmail('');
                        setCurrentPage(0);
                        setOwnersPage(0);
                        refetchOwners();
                      }}
                      className="btn btn-ghost gap-2"
                    >
                      <XMarkIcon className="h-5 w-5" />
                      {t('common.clear')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {topReferrersError && (
              <div className="alert alert-error mb-4">
                <XCircleIcon className="h-5 w-5" />
                <span>{topReferrersError.message}</span>
              </div>
            )}

            {(
              activeView === 'referrers' ? topReferrersLoading : ownersLoading
            ) ? (
              <div className="flex justify-center py-8">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : (
              <div className="space-y-2">
                {activeView === 'owners'
                  ? (isSearchMode
                      ? searchOwnersData?.search_owners?.data?.owners
                      : ownersData?.list_owners?.data?.owners
                    )?.map((owner: AdminOwner) => (
                      <motion.div
                        key={owner._id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`cursor-pointer rounded-lg border p-4 transition-all ${
                          selectedReferrer?._id === owner._id
                            ? 'border-primary bg-primary/5'
                            : 'border-base-300 hover:border-primary/50'
                        }`}
                        onClick={() => {
                          setSelectedReferrer(owner as any);
                          setReferralsPage(0);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">
                                {owner.first_name || owner.last_name
                                  ? `${owner.first_name || ''} ${
                                      owner.last_name || ''
                                    }`.trim()
                                  : owner.email}
                              </h3>
                              {owner.verified && (
                                <CheckCircleIcon className="text-success h-4 w-4" />
                              )}
                              {owner.allow_referral && (
                                <span className="badge badge-success badge-sm">
                                  {t('referrals.active_referrer')}
                                </span>
                              )}
                            </div>
                            <p className="text-base-content/70 text-sm">
                              {owner.email}
                            </p>
                            <div className="mt-2 flex items-center gap-4">
                              {owner.referral_code && (
                                <span className="font-mono text-sm">
                                  {t('referrals.code')}: {owner.referral_code}
                                </span>
                              )}
                              <span className="text-sm">
                                {t('referrals.status')}: {owner.status}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!owner.allow_referral && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActivateReferral(owner._id);
                                }}
                                disabled={activatingReferral}
                                className="btn btn-sm btn-success gap-1"
                              >
                                {activatingReferral ? (
                                  <span className="loading loading-spinner loading-xs"></span>
                                ) : (
                                  <PlusIcon className="h-4 w-4" />
                                )}
                                {t('referrals.activate')}
                              </button>
                            )}
                            <ChevronRightIcon className="text-base-content/50 h-5 w-5" />
                          </div>
                        </div>
                      </motion.div>
                    ))
                  : topReferrersData?.list_top_referrers?.data?.referrers?.map(
                      (referrer: any) => (
                        <motion.div
                          key={referrer._id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`cursor-pointer rounded-lg border p-4 transition-all ${
                            selectedReferrer?._id === referrer._id
                              ? 'border-primary bg-primary/5'
                              : 'border-base-300 hover:border-primary/50'
                          }`}
                          onClick={() => {
                            setSelectedReferrer(referrer);
                            setReferralsPage(0);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold">
                                  {referrer.first_name || referrer.last_name
                                    ? `${referrer.first_name || ''} ${
                                        referrer.last_name || ''
                                      }`.trim()
                                    : referrer.email}
                                </h3>
                                {referrer.verified && (
                                  <CheckCircleIcon className="text-success h-4 w-4" />
                                )}
                              </div>
                              <p className="text-base-content/70 text-sm">
                                {referrer.email}
                              </p>
                              <div className="mt-2 flex items-center gap-4">
                                <span className="text-sm">
                                  <span className="text-primary font-medium">
                                    {referrer.effective_referrals_count}
                                  </span>{' '}
                                  {t('referrals.effective')}
                                </span>
                                <span className="text-sm">
                                  <span className="font-medium">
                                    {referrer.total_referrals_count}
                                  </span>{' '}
                                  {t('referrals.total')}
                                </span>
                              </div>
                            </div>
                            <ChevronRightIcon className="text-base-content/50 h-5 w-5" />
                          </div>
                        </motion.div>
                      ),
                    )}

                {/* Pagination */}
                {(activeView === 'owners'
                  ? isSearchMode
                    ? searchOwnersData?.search_owners?.data?.hasMore
                    : ownersData?.list_owners?.data?.pagination
                  : topReferrersData?.list_top_referrers?.data?.pagination) && (
                  <div className="mt-6 flex justify-center gap-2">
                    <button
                      onClick={() => {
                        if (activeView === 'owners') {
                          setOwnersPage(Math.max(0, ownersPage - 1));
                        } else {
                          setCurrentPage(Math.max(0, currentPage - 1));
                        }
                      }}
                      disabled={
                        activeView === 'owners'
                          ? ownersPage === 0
                          : currentPage === 0
                      }
                      className="btn btn-sm btn-outline"
                    >
                      {t('common.previous')}
                    </button>
                    <span className="btn btn-sm btn-ghost cursor-default">
                      {t('common.page')}{' '}
                      {(activeView === 'owners' ? ownersPage : currentPage) + 1}
                    </span>
                    <button
                      onClick={() => {
                        if (activeView === 'owners') {
                          setOwnersPage(ownersPage + 1);
                        } else {
                          setCurrentPage(currentPage + 1);
                        }
                      }}
                      disabled={
                        activeView === 'owners'
                          ? isSearchMode
                            ? !searchOwnersData?.search_owners?.data?.hasMore
                            : !ownersData?.list_owners?.data?.pagination
                                ?.hasMore
                          : !topReferrersData?.list_top_referrers?.data
                              ?.pagination?.hasMore
                      }
                      className="btn btn-sm btn-outline"
                    >
                      {t('common.next')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Referral Details */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title">
              {selectedReferrer
                ? t('referrals.referral_details')
                : t('referrals.select_referrer')}
            </h2>

            {selectedReferrer ? (
              <>
                {/* Referrer Info */}
                <div className="bg-base-200 mb-4 rounded-lg p-4">
                  <h3 className="mb-2 font-semibold">
                    {selectedReferrer.first_name || selectedReferrer.last_name
                      ? `${selectedReferrer.first_name || ''} ${
                          selectedReferrer.last_name || ''
                        }`.trim()
                      : selectedReferrer.email}
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-base-content/70">
                        {t('referrals.referral_code')}:
                      </span>
                      <span className="ml-1 font-mono font-medium">
                        {selectedReferrer.referral_code || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-base-content/70">
                        {t('referrals.status')}:
                      </span>
                      <span
                        className={`badge badge-sm ml-1 ${getStatusBadgeClass(
                          selectedReferrer.status,
                        )}`}
                      >
                        {selectedReferrer.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Referrals List */}
                {referralDetailsLoading ? (
                  <div className="flex justify-center py-8">
                    <span className="loading loading-spinner loading-lg"></span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referralDetailsData?.get_referrals_by_referrer?.data?.referrals?.map(
                      (referral: any) => (
                        <div
                          key={referral._id}
                          className="border-base-300 rounded-lg border p-3"
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {referral.referee_details?.first_name ||
                                referral.referee_details?.last_name
                                  ? `${
                                      referral.referee_details.first_name || ''
                                    } ${
                                      referral.referee_details.last_name || ''
                                    }`.trim()
                                  : referral.referee_details?.email ||
                                    t('referrals.unknown_user')}
                              </span>
                              {referral.referee_details?.verified && (
                                <CheckCircleIcon className="text-success h-4 w-4" />
                              )}
                            </div>
                            <span
                              className={`badge badge-sm ${getStatusBadgeClass(
                                referral.status,
                              )}`}
                            >
                              {referral.status}
                            </span>
                          </div>
                          <div className="text-base-content/70 text-sm">
                            <p>{referral.referee_details?.email}</p>
                            <p>
                              {t('referrals.created_at')}:{' '}
                              {formatDate(referral.created_at)}
                            </p>
                            {referral.rejected && (
                              <p className="text-error">
                                {t('referrals.rejected')}:{' '}
                                {referral.rejected.reason}
                              </p>
                            )}
                          </div>
                        </div>
                      ),
                    )}

                    {/* Referrals Pagination */}
                    {referralDetailsData?.get_referrals_by_referrer?.data
                      ?.pagination && (
                      <div className="mt-4 flex justify-center gap-2">
                        <button
                          onClick={() =>
                            setReferralsPage(Math.max(0, referralsPage - 1))
                          }
                          disabled={referralsPage === 0}
                          className="btn btn-sm btn-outline"
                        >
                          {t('common.previous')}
                        </button>
                        <span className="btn btn-sm btn-ghost cursor-default">
                          {t('common.page')} {referralsPage + 1}
                        </span>
                        <button
                          onClick={() => setReferralsPage(referralsPage + 1)}
                          disabled={
                            !referralDetailsData.get_referrals_by_referrer.data
                              .pagination.hasMore
                          }
                          className="btn btn-sm btn-outline"
                        >
                          {t('common.next')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-base-content/50 flex flex-col items-center justify-center py-12">
                <UserGroupIcon className="mb-4 h-16 w-16" />
                <p>{t('referrals.select_referrer_hint')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralsDashboard;
